import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const EMPTY_AUTO = {
  case_type_name: "", case_type_id: "",
  department_name: "", department_id: "",
  priority: "", sla_minutes: 0, sla_label: "",
  branch_required: false,
};

/**
 * useTaxonomyCascade — encapsulates the Main → Sub → Issue Type cascade
 * (lookup fetches + preview/auto-fill) so dialogs can stay UI-focused.
 *
 * Args:
 *   enabled: bool — if false, no API calls are issued (used to defer until dialog opens)
 *   mainId, subId, issueId: currently selected IDs (driven by form state)
 *
 * Returns:
 *   mainCats[], subCats[], issueTypes[] — option lists for the three selects
 *   auto — { case_type_*, department_*, priority, sla_minutes, sla_label, branch_required }
 *   mainName, subName, issueName — display labels for the current selection
 */
export function useTaxonomyCascade({ enabled, mainId, subId, issueId }) {
  const [mainCats, setMainCats] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [auto, setAuto] = useState(EMPTY_AUTO);

  // Load main categories once when enabled
  useEffect(() => {
    if (!enabled) return;
    api.get("/main-categories").then((r) => setMainCats(r.data.filter((x) => x.active)));
  }, [enabled]);

  // mainId → subCats
  useEffect(() => {
    if (!mainId) { setSubCats([]); setIssueTypes([]); return; }
    api.get("/sub-categories", { params: { main_category_id: mainId } })
      .then((r) => setSubCats(r.data.filter((x) => x.active)));
  }, [mainId]);

  // subId → issueTypes
  useEffect(() => {
    if (!subId) { setIssueTypes([]); return; }
    api.get("/issue-types", { params: { sub_category_id: subId } })
      .then((r) => setIssueTypes(r.data.filter((x) => x.active)));
  }, [subId]);

  // issueId → auto-fill preview
  useEffect(() => {
    if (!issueId) { setAuto(EMPTY_AUTO); return; }
    api.get(`/issue-types/${issueId}/preview`).then((r) => {
      const p = r.data;
      setAuto({
        case_type_name: p.case_type?.name || "",
        case_type_id: p.case_type?.id || "",
        department_name: p.department?.name || "",
        department_id: p.department?.id || "",
        priority: p.priority,
        sla_minutes: p.sla_minutes,
        sla_label: p.sla_label,
        branch_required: !!p.branch_required,
      });
    });
  }, [issueId]);

  const mainName = useMemo(() => mainCats.find((m) => m.id === mainId)?.name || "", [mainCats, mainId]);
  const subName = useMemo(() => subCats.find((s) => s.id === subId)?.name || "", [subCats, subId]);
  const issueName = useMemo(() => issueTypes.find((i) => i.id === issueId)?.name || "", [issueTypes, issueId]);

  return { mainCats, subCats, issueTypes, auto, mainName, subName, issueName };
}
